import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  WALLET_TRANSACTIONS_PAGE_SIZE_OPTIONS,
  WalletTransactionsPageSize
} from '@/components-latest/ui/BillingPage/WalletTransactions/constants'

import Styles from './TransactionsList.module.scss'
import { getVisiblePages } from './getVisiblePages'

type Props = {
  page: number
  limit: WalletTransactionsPageSize
  offset: number
  itemCount: number
  total?: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: WalletTransactionsPageSize) => void
}

const TransactionsListFooter = ({
  page,
  limit,
  offset,
  itemCount,
  total,
  onPageChange,
  onLimitChange
}: Props) => {
  const { t } = useTranslation()

  const hasMore =
    total != null ? offset + itemCount < total : itemCount >= limit
  const resolvedTotal =
    total ?? (hasMore ? offset + itemCount + 1 : offset + itemCount)
  const totalPages = Math.max(1, Math.ceil(resolvedTotal / limit))
  const rangeStart = itemCount > 0 ? offset + 1 : 0
  const rangeEnd = offset + itemCount

  const visiblePages = useMemo(
    () => getVisiblePages(page, totalPages),
    [page, totalPages]
  )

  const rangeLabel =
    total != null
      ? t('billing.wallet.pagination.range', {
          start: rangeStart,
          end: rangeEnd,
          total
        })
      : t('billing.wallet.pagination.range-estimated', {
          start: rangeStart,
          end: rangeEnd,
          total: resolvedTotal
        })

  return (
    <div className={Styles.footer} data-testid="wallet-transactions-pagination">
      <div className={Styles.rowsPerPage}>
        <span>{t('billing.wallet.pagination.rows-per-page')}</span>
        <div className={Styles.rowsPerPageSelectWrapper}>
          <select
            className={Styles.rowsPerPageSelect}
            value={limit}
            aria-label={t('billing.wallet.pagination.rows-per-page')}
            onChange={event =>
              onLimitChange(
                Number(event.target.value) as WalletTransactionsPageSize
              )
            }>
            {WALLET_TRANSACTIONS_PAGE_SIZE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <ChevronDown width={16} height={16} className={Styles.selectIcon} />
        </div>
      </div>

      <span className={Styles.rangeLabel}>{rangeLabel}</span>

      <div className={Styles.pagination}>
        <button
          type="button"
          className={Styles.pageNavButton}
          disabled={page <= 0}
          aria-label={t('billing.wallet.pagination.previous')}
          onClick={() => onPageChange(page - 1)}>
          <ChevronLeft width={16} height={16} />
        </button>

        {visiblePages.map((pageItem, index) =>
          pageItem === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className={Styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={pageItem}
              type="button"
              className={`${Styles.pageButton} ${
                pageItem === page ? Styles.activePageButton : ''
              }`}
              onClick={() => onPageChange(pageItem)}>
              {pageItem + 1}
            </button>
          )
        )}

        <button
          type="button"
          className={`${Styles.pageNavButton} ${
            page < totalPages - 1 ? Styles.pageNavButtonEnabled : ''
          }`}
          disabled={page >= totalPages - 1 || !hasMore}
          aria-label={t('billing.wallet.pagination.next')}
          onClick={() => onPageChange(page + 1)}>
          <ChevronRight width={16} height={16} />
        </button>
      </div>
    </div>
  )
}

export default TransactionsListFooter
